import { useConfig } from '@/hooks/useConfig/useConfig';
import { useTransaction } from '@/hooks/useTransaction/useTransaction';
import { useAirKeyTrace, useAirKeyTracePageView } from '@/utils/airKeyTrace';
import { cn } from '@/utils/cn';
import { getAlertText } from '@/utils/getAlertText';
import {
  Button,
  Column,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  TextBody,
  TextHeading,
} from '@cof/omni-react';

export const IncorrectCard = ({ onAccept }: { onAccept: () => void }) => {
  useAirKeyTracePageView('incorrect_card_modal');

  const { publicData } = useTransaction();
  const { locale } = useConfig();
  const sendTrace = useAirKeyTrace();

  const cards = publicData?.cardLast4Array || [];

  const translations: {
    [k: string]: {
      title: string;
      body: (cards: string[]) => React.ReactNode;
      button: string;
    };
  } = {
    'en-US': {
      title: 'Use a different card',
      body: (c) => (
        <>
          It looks like that wasn't the right card. Double check that you're
          using {getAlertText(c)} and try again.
        </>
      ),
      button: 'Try again',
    },
    'es-US': {
      title: 'Usa otra tarjeta',
      body: (c) => (
        <>
          Parece que no era la tarjeta correcta. Verifique que está usando
          {getAlertText(c)} y vuelva a intentarlo.
        </>
      ),
      button: 'Intentar de nuevo',
    },
  };

  const t = translations[locale] || translations['en-US'];

  return (
    <Modal open={true}>
      <Dialog className={cn('bg-base1')}>
        <DialogHeader paddingBottom="zero">
          <TextHeading
            fontWeight="semibold"
            className={cn('text-neutral-primary')}
          >
            {t.title}
          </TextHeading>
        </DialogHeader>
        <DialogContent paddingBottom="tiny">
          <Column>
            <TextBody className={cn('text-neutral-primary')}>
              {t.body(cards)}
            </TextBody>
          </Column>
        </DialogContent>
        <DialogFooter padding="medium">
          <Column padding="small" alignItems="flex-end">
            <Button
              size="medium"
              className={cn(
                'text-neutral-inverse',
                'bg-interactive-emphasis-high',
              )}
              rank="primary"
              onClick={() => {
                sendTrace({
                  view: 'incorrect_card_modal',
                  action: 'retry',
                });
                onAccept();
              }}
            >
              {t.button}
            </Button>
          </Column>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
};
